import { useTranslation } from '@lyearn/i18n';
import * as ContentListModal from '@/components/ContentListModal';
import ContentSelector from '@/modules/corporate//modules/path/views/PathEditor/components/ContentSelector';
import { adaptContent } from '@/modules/corporate//modules/path/views/PathEditor/helpers';
import {
  AssignmentTypeEnum,
  ContentCollectionGroupType,
  ContentEntity,
  EventSelectionInput,
  EventUnion,
} from '@/types/schema';

import CollectionSelector from './CollectionSelector';
import SessionSelector from './SessionSelector';
import { Tabs } from './types';
import useContentsDialog from './useContentsDialog';

const getIsAlreadySelected = ({
  disabledItem,
  isAlreadySelected,
  isInitiallySelected,
}: {
  disabledItem?: boolean;
  isAlreadySelected: boolean;
  isInitiallySelected: boolean;
}): boolean => disabledItem || (isAlreadySelected && isInitiallySelected);

const ContentListModalBody = ({
  data,
  isAlreadySelected,
  selectedContentIds,
  selectedCollectionIds,
  eventSelections,
  isMultiAllowed,
  inViewTab,
  disabledIds,
  childSessionsMap,
  getAssignmentType,
  isAssingmentTypeChangeAllowed,
  showDuration,
}: {
  data: ReturnType<typeof useContentsDialog>['data'];
  selectedContentIds: string[];
  selectedCollectionIds: string[];
  eventSelections: EventSelectionInput[];
  isAlreadySelected: boolean;
  isMultiAllowed: boolean;
  inViewTab: Tabs;
  disabledIds?: string[];
  childSessionsMap: ReturnType<typeof useContentsDialog>['childSessionsMap'];
  getAssignmentType: ReturnType<typeof useContentsDialog>['getAssignmentType'];
  isAssingmentTypeChangeAllowed?: boolean;
  showDuration: boolean;
}) => {
  const { t } = useTranslation();

  return (
    <ContentListModal.Body
      className="flex w-full flex-1 flex-col border-b border-interactive-secondary-hover"
      itemRenderer={(index: number) => {
        const currentContent = data.adaptedContents[index];
        if (!currentContent) {
          return null;
        }

        const { id } = currentContent;
        const disabledItem = disabledIds?.includes(id);

        if (inViewTab === Tabs.Content) {
          return (
            <ContentSelector
              key={id}
              content={adaptContent(t, currentContent as ContentEntity)}
              isAlreadySelected={getIsAlreadySelected({
                disabledItem,
                isAlreadySelected,
                isInitiallySelected: selectedContentIds!.includes(id),
              })}
              isSelected={data.has(id)}
              multiSelect={isMultiAllowed}
              onClick={() => data.toggle(id)}
            />
          );
        } else if (inViewTab === Tabs.Collections) {
          return (
            <CollectionSelector
              key={id}
              collection={currentContent as ContentCollectionGroupType}
              isAlreadySelected={getIsAlreadySelected({
                disabledItem,
                isAlreadySelected,
                isInitiallySelected: selectedCollectionIds!.includes(id),
              })}
              isSelected={data.has(id)}
              multiSelect={isMultiAllowed}
              toggle={data.toggle}
            />
          );
        }

        const initialEventSelection = eventSelections.find((selection) => selection.eventId === id);
        const childSessions = childSessionsMap[id];

        return (
          <>
            <SessionSelector
              key={id}
              assignmentType={getAssignmentType(id)}
              event={currentContent as EventUnion}
              isAlreadySelected={getIsAlreadySelected({
                disabledItem,
                isAlreadySelected,
                isInitiallySelected: Boolean(initialEventSelection),
              })}
              isAssingmentTypeChangeAllowed={isAssingmentTypeChangeAllowed}
              isSelected={data.has(id)}
              multiSelect={isMultiAllowed}
              showDuration={showDuration}
              toggle={data.toggle}
            />
            {childSessions
              ? childSessions.map((childSession) => {
                  const { id } = childSession;
                  const initialEventSelection = eventSelections.find(
                    (selection) => selection.eventId === id,
                  );

                  return (
                    <SessionSelector
                      key={id}
                      assignmentType={getAssignmentType(id, AssignmentTypeEnum.Specific)}
                      className="ps-48"
                      event={childSession}
                      isAlreadySelected={getIsAlreadySelected({
                        disabledItem,
                        isAlreadySelected,
                        isInitiallySelected: Boolean(initialEventSelection),
                      })}
                      isSelected={data.has(id)}
                      multiSelect={isMultiAllowed}
                      showDuration={showDuration}
                      toggle={data.toggle}
                    />
                  );
                })
              : null}
          </>
        );
      }}
    />
  );
};

export default ContentListModalBody;
